import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { Button, Stack, useMediaQuery } from '@mui/material';
import { useLiveQuery } from 'dexie-react-hooks';
import { t } from 'i18next';
import { useLoadPricingPage } from '../../app/useLoadPricingPage';
import { getCurrentMailboxQuery } from '../../model/mailbox';
import AnalyticsService from '../../services/analytics/analyticsService';
import { EventName } from '../../services/analytics/providers/analyticsProvider';
import theme from '../../theme';
import { usePricingPageActions } from '../PricingPage/usePricingPageActions';
import FeatureBullet from '../../components/FeatureBullet/FeatureBullet';
import Testimonial from '../../components/Testimonial/Testimonial';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import CheckIcon from '@mui/icons-material/Check';
import PulsingActionButton from '../../components/common/PulsingActionButton';
import { useGetOffering } from '../../app/useGetOffering';
import { PlanTypeForCheckout } from '../../features/offering/offeringSlice.types';
import { usePaywall } from '../../providers/PaywallProvider';

export type PaywallPageProps = {
  userEmailFromURL?: string;
  nativeAppFunnelMode?: boolean;
};

const ALLOW_SKIP = false;

export default function PaywallPage(props: PaywallPageProps) {
  const { userEmailFromURL } = props;

  const vh = window.innerHeight * 0.01;
  // Set the CSS variable to the root element
  document.documentElement.style.setProperty('--vh', vh + 'px');

  const mailbox = useLiveQuery(getCurrentMailboxQuery);

  const userEmail = mailbox?.email_address || userEmailFromURL;

  const aboveMedium = useMediaQuery(theme.breakpoints.up('md'));
  const aboveSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('xs'));

  const { discountPercentage, includeLifetime, promoCode, returnUrl, source } = useLoadPricingPage(
    mailbox,
    ''
  );

  const { hasPremium } = usePaywall();

  // If user already has premium, redirect to install
  useEffect(() => {
    if (hasPremium) {
      window.location.href = returnUrl;
    }
  }, [hasPremium]);

  const { onPlanCheckout, onDecline } = usePricingPageActions({
    userEmail,
    discountPercentage,
    includeLifetime,
    promoCode,
    returnUrl,
    source,
    aboveMedium,
    trialPeriodDays: 7,
  });

  const { currency } = useGetOffering({ mailbox });

  useEffect(() => {
    AnalyticsService.track(EventName.UPGRADE_PAGE_VISIT);
  }, []);

  const features = [
    {
      icon: <EmailIcon />,
      text: 'Unsubscribe From All Junk Mail',
    },
    {
      icon: <DeleteIcon />,
      text: 'Bulk Delete Old Emails',
    },
    // {
    //   icon: <PhotoLibraryIcon />,
    //   text: 'Delete Similar & Duplicate Photos',
    // },
    {
      icon: <SecurityIcon />,
      text: 'Complete Data Privacy',
    },
  ];

  return (
    <Stack
      sx={{
        height: 'calc(var(--vh) * 100)',
        background: 'linear-gradient(to bottom, #a1c4fd 0%, #c2e9fb 40%, #f9f9f9 70%)',
      }}
      className='full-height-stack'
    >
      <Container
        sx={{
          pt: { xs: 6, sm: 8, md: 10 },
          pb: { xs: 2, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography
          component='h1'
          variant='h2'
          align='center'
          color='text.primary'
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
            mb: { xs: 4, sm: 6 },
            whiteSpace: { xs: 'pre-line', sm: 'normal' },
          }}
        >
          {t('PAYWALL_TITLE')}
        </Typography>

        <Stack
          direction='column'
          spacing={2}
          alignItems='stretch'
          sx={{
            width: isExtraSmall ? '100%' : 'unset',
            maxWidth: '500px',
            px: { xs: 2, sm: 4 },
          }}
        >
          {features.map((feature, index) => (
            <FeatureBullet key={index} icon={feature.icon} text={feature.text} />
          ))}
        </Stack>

        <Testimonial
          text='I had over 45,000 emails 😅 This app made it easy and simple to sort my junk email for me and help me delete and unsubscribe! Thank you!!'
          author='Drjdizzle'
        />

        <Box
          sx={{
            width: '100%',
            textAlign: 'center',
            justifyItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={1}
            sx={{ mb: 2 }}
          >
            <CheckIcon sx={{ color: 'black', fontSize: { xs: '1.2rem', sm: '1.4rem' } }} />
            <Typography
              align='center'
              sx={{
                color: 'black',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 500,
              }}
            >
              No Payment Due Now
            </Typography>
          </Stack>
          <PulsingActionButton
            width='90%'
            onClick={() => {
              if (!currency) {
                console.error('No selected plan or currency');
                return;
              }

              const planType: PlanTypeForCheckout = {
                planType: {
                  label: 'default',
                  amount: 39.99,
                  interval: 'year',
                },
                amountWithDiscount: 39.99,
                amountWithoutDiscount: 39.99,
                interval: 'year',
              };
              onPlanCheckout(planType, currency, undefined);
            }}
          >
            {t('continue')}
          </PulsingActionButton>
          <Typography
            align='center'
            color='text.secondary'
            sx={{
              mb: 1,
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            Just $39.99 per year ($3.33/mo)
          </Typography>
        </Box>
      </Container>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {ALLOW_SKIP && (
          <Button
            variant='text'
            size='medium'
            color='gray'
            sx={{
              margin: aboveSmall ? '1rem' : isExtraSmall ? '0.5rem' : '0.75rem',
            }}
            onClick={onDecline}
          >
            {t('notNow')}
          </Button>
        )}
      </Box>
    </Stack>
  );
}
